'use client';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { api } from '@/lib/api';

export default function DashboardPage() {
  const router = useRouter();
  const [data, setData] = useState<any>(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    api.dashboard().then((r: any) => setData(r?.data)).catch(() => {}).finally(() => setBusy(false));
  }, []);

  if (busy) return <div className="p-8 text-muted-foreground">Загрузка…</div>;

  const cards = [
    { label: 'Компаний', value: data?.companies ?? 0, sub: `${data?.activeCompanies ?? 0} активных`, color: 'bg-blue-500', href: '/companies' },
    { label: 'Пользователей', value: data?.users ?? 0, sub: 'всего на платформе', color: 'bg-emerald-500', href: '/users' },
    { label: 'Тестов', value: data?.tests ?? 0, sub: 'опубликовано', color: 'bg-violet-500', href: '/tests' },
    { label: 'Прохождений', value: data?.testResults ?? 0, sub: 'тестов пройдено', color: 'bg-amber-500' },
  ];

  return (
    <div className="mx-auto max-w-5xl p-6">
      <h1 className="mb-6 text-2xl font-bold">Дашборд</h1>

      {/* Metrics */}
      <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((c) => (
          <button
            key={c.label}
            onClick={() => c.href && router.push(c.href)}
            className="rounded-xl border border-border bg-card p-5 text-left transition hover:shadow-sm"
          >
            <div className={`mb-2 inline-flex h-8 w-8 items-center justify-center rounded-lg ${c.color}`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M3 3h18v18H3zM3 9h18M9 21V9"/></svg>
            </div>
            <div className="text-2xl font-bold">{c.value}</div>
            <div className="text-sm text-muted-foreground">{c.label}</div>
            <div className="mt-1 text-xs text-muted-foreground">{c.sub}</div>
          </button>
        ))}
      </div>

      {/* Tariffs */}
      <div className="mb-8 rounded-xl border border-border bg-card p-5">
        <h2 className="mb-4 text-sm font-bold">Тарифы компаний</h2>
        {data?.tariffDistribution && Object.keys(data.tariffDistribution).length > 0 ? (
          <div className="space-y-2">
            {Object.entries(data.tariffDistribution).map(([tariff, count]) => (
              <div key={tariff} className="flex items-center gap-3">
                <span className="w-24 text-sm capitalize">{tariff}</span>
                <div className="h-3 flex-1 overflow-hidden rounded-full bg-muted">
                  <div className="h-full bg-primary" style={{ width: `${Math.min(100, (Number(count) / (data?.companies || 1)) * 100)}%` }} />
                </div>
                <span className="w-8 text-right text-sm font-bold">{count as number}</span>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-muted-foreground">Нет данных</p>}
      </div>

      {/* Recent companies */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h2 className="mb-4 text-sm font-bold">Последние компании</h2>
        <div className="space-y-2">
          {(data?.recentCompanies ?? []).map((c: any) => (
            <button
              key={c.id}
              onClick={() => router.push(`/companies/${c.id}`)}
              className="flex w-full items-center justify-between rounded-lg border border-border px-3 py-2 text-left hover:bg-muted"
            >
              <span className="text-sm font-medium">{c.name}</span>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                  c.tariff === 'pro' ? 'bg-violet-100 text-violet-700' :
                  c.tariff === 'basic' ? 'bg-blue-100 text-blue-700' :
                  'bg-muted text-muted-foreground'
                }`}>{c.tariff || 'free'}</span>
                <span className="text-xs text-muted-foreground">{new Date(c.createdAt).toLocaleDateString('ru-RU')}</span>
              </div>
            </button>
          ))}
          {(data?.recentCompanies ?? []).length === 0 && <p className="text-sm text-muted-foreground">Нет компаний</p>}
        </div>
      </div>
    </div>
  );
}
